import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { useCart } from '../context/CartContext';

function CustomerUI() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { cartItemCount, isCartOpen, toggleCart, closeCart } = useCart();

  const fetchPages = async () => {
    try {
      const response = await apiClient.get('/pages/public');
      setPages(response.data);
    } catch (error) {
      console.error('Sayfalar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPages();
    // Her 30 saniyede bir sayfaları güncelle (aktif/pasif durumu için)
    const interval = setInterval(fetchPages, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="text-4xl">🛍️</span>
            <div>
              <h1 className="text-2xl font-extrabold text-blue-600">BARASCOPY</h1>
              <p className="text-xs text-gray-500">STORE</p>
            </div>
          </Link>
          
          <nav className="flex space-x-6 items-center">
            <NavLink to="/" label="Ana Sayfa" icon="🏠" />
            {pages.map(page => (
              <NavLink key={page.id} to={`/sayfa/${page.slug}`} label={page.title} icon={page.icon || '📄'} />
            ))}
            <NavLink to="/duyurular" label="Duyurular" icon="📢" />
            <NavLink to="/iletisim" label="İletişim" icon="📞" />
            
            {/* Sepet Butonu */}
            <button 
              onClick={toggleCart}
              className="relative p-2 text-gray-700 hover:text-blue-600 transition"
            >
              <span className="text-2xl">🛒</span>
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {cartItemCount > 99 ? '99+' : cartItemCount}
                </span>
              )}
            </button>
          </nav>
        </div>
      </header>

      {/* Cart Sidebar */}
      <CartSidebar isOpen={isCartOpen} onClose={closeCart} />

      {/* Main Content */}
      <main>
        <Routes>
          <Route path="/" element={<CustomerHome pages={pages} loading={loading} />} />
          <Route path="/sayfa/:slug" element={<PageDetail />} />
          <Route path="/duyurular" element={<AnnouncementsPage />} />
          <Route path="/iletisim" element={<ContactPage />} />
          <Route path="/siparis" element={<OrderPage />} />
          <Route path="/siparis-onay" element={<OrderConfirmation />} />
        </Routes>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12 mt-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-3xl">🛍️</span>
                <span className="text-xl font-bold">BARASCOPY</span>
              </div>
              <p className="text-gray-400">Kaliteli ürünler, uygun fiyatlar.</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Hızlı Bağlantılar</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/" className="hover:text-white">Ana Sayfa</Link></li>
                <li><Link to="/iletisim" className="hover:text-white">İletişim</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">İletişim</h4>
              <ul className="space-y-2 text-gray-400">
                <li>📍 Davutpaşa Mah. Davutpaşa Cad. No: 151/B Esenler/İstanbul</li>
                <li>📧 barascopy@gmail.com</li>
                <li>📞 0545 547 4751 - 0545 547 4793</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2026 BARASCOPY . Tüm hakları saklıdır.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function NavLink({ to, label, icon }) {
  return (
    <Link to={to} className="flex items-center gap-2 text-gray-700 hover:text-blue-600 font-semibold transition">
      <span>{icon}</span>
      <span>{label}</span>
    </Link>
  );
}

// ==================== ANA SAYFA ====================
function CustomerHome({ pages, loading }) {
  const navigate = useNavigate();

  return (
    <div>
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-extrabold mb-4">
            Hoş Geldiniz! 🎉
          </h2>
          <p className="text-xl opacity-90 mb-8">
            Kırtasiye, teknoloji ve daha fazlası - hepsi bir yerde
          </p>
          <button
            onClick={() => navigate('/iletisim')}
            className="px-8 py-3 bg-white text-blue-600 font-bold rounded-lg hover:bg-gray-100 transition transform hover:scale-105"
          >
            📞 Bize Ulaşın
          </button>
        </div>
      </div>

      {/* Categories */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <h3 className="text-3xl font-bold text-center mb-12">📦 Ürün Kategorilerimiz</h3>
        
        {loading ? (
          <p className="text-center text-gray-500">Yükleniyor...</p>
        ) : pages.length === 0 ? (
          <p className="text-center text-gray-500">Henüz kategori bulunmamaktadır.</p>
        ) : (
          <div className="grid md:grid-cols-3 gap-8">
            {pages.map((page, index) => (
              <CategoryCard 
                key={page.id}
                page={page}
                color={['blue', 'green', 'purple', 'orange', 'pink'][index % 5]}
              />
            ))}
          </div>
        )}
      </div>

      {/* Features */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-3xl font-bold text-center mb-12">Neden Bizi Tercih Etmelisiniz?</h3>
          <div className="grid md:grid-cols-4 gap-8">
            <FeatureCard icon="💰" title="Uygun Fiyat" description="En rekabetçi fiyatlarla kaliteli ürünler" />
            <FeatureCard icon="🚚" title="Hızlı Teslimat" description="Siparişleriniz en kısa sürede hazır" />
            <FeatureCard icon="✅" title="Güvenilir" description="Yıllardır güvenle hizmet veriyoruz" />
            <FeatureCard icon="💬" title="7/24 Destek" description="Sorularınız için her zaman yanınızdayız" />
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-12 text-white text-center">
          <h3 className="text-3xl font-bold mb-4">İlgilendiğiniz ürün mü var?</h3>
          <p className="text-lg opacity-90 mb-6">Hemen bizimle iletişime geçin, size en kısa sürede dönelim!</p>
          <button
            onClick={() => navigate('/iletisim')}
            className="px-8 py-3 bg-white text-blue-600 font-bold rounded-lg hover:bg-gray-100 transition"
          >
            📞 İletişim Formu
          </button>
        </div>
      </div>
    </div>
  );
}

function CategoryCard({ page, color }) {
  const navigate = useNavigate();
  
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600',
    pink: 'from-pink-500 to-pink-600',
  };

  return (
    <div 
      onClick={() => navigate(`/sayfa/${page.slug}`)}
      className="bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer transform transition hover:scale-105 hover:shadow-xl"
    >
      <div className={`bg-gradient-to-r ${colorClasses[color]} h-32 flex items-center justify-center`}>
        <span className="text-6xl">{page.icon || '📄'}</span>
      </div>
      <div className="p-6">
        <h4 className="text-xl font-bold text-gray-800 mb-2">{page.title}</h4>
        <p className="text-gray-600 text-sm mb-4">{page.description}</p>
        <button className="text-blue-600 font-semibold hover:text-blue-800">
          Görüntüle →
        </button>
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="text-center p-6">
      <span className="text-5xl mb-4 block">{icon}</span>
      <h4 className="text-lg font-bold text-gray-800 mb-2">{title}</h4>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  );
}

// ==================== SAYFA DETAY ====================
function PageDetail() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [addedToCart, setAddedToCart] = useState(null);

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    try {
      const response = await apiClient.get(`/pages/public/${slug}`);
      setPage(response.data);
    } catch (error) {
      console.error('Sayfa yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  // Kategoriye ve arama sorgusuna göre filtrelenmiş ürünler
  const filteredImages = page?.images?.filter(img => {
    const categoryMatch = !selectedCategory || img.category_id === parseInt(selectedCategory);
    const searchMatch = !searchQuery || 
      img.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      img.barcode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      img.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return categoryMatch && searchMatch;
  }) || [];

  const handleAddToCart = (e, image) => {
    e.stopPropagation();
    addToCart({
      id: image.id,
      title: image.title,
      price: image.price,
      image_url: image.image_url,
      barcode: image.barcode,
      page_title: page.title
    });
    setAddedToCart(image.id);
    setTimeout(() => setAddedToCart(null), 1500);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 text-center">
        <p className="text-gray-500">Yükleniyor...</p>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Sayfa Bulunamadı</h2>
        <button onClick={() => navigate('/')} className="text-blue-600 hover:text-blue-800">
          ← Ana Sayfaya Dön
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      {/* Breadcrumb */}
      <div className="mb-8">
        <Link to="/" className="text-blue-600 hover:text-blue-800">Ana Sayfa</Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-600">{page.title}</span>
      </div>

      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-800 mb-4">{page.title}</h1>
        <p className="text-lg text-gray-600">{page.description}</p>
      </div>

      {/* Search Box */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="🔍 Ürün adı veya barkod ile ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-6 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-lg"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Category Filter */}
      {page.categories && page.categories.length > 0 && (
        <div className="mb-8 bg-white rounded-xl shadow-md p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="font-semibold text-gray-700">📁 Kategori:</span>
            <button
              onClick={() => setSelectedCategory('')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                selectedCategory === '' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tümü ({page.images?.length || 0})
            </button>
            {page.categories.map((cat) => {
              const count = page.images?.filter(img => img.category_id === cat.id).length || 0;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id.toString())}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition flex items-center gap-2 ${
                    selectedCategory === cat.id.toString()
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>{cat.icon || '📁'}</span>
                  <span>{cat.name}</span>
                  <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Products Grid */}
      {filteredImages.length > 0 ? (
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
          {filteredImages.map((image) => (
            <div 
              key={image.id} 
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition cursor-pointer relative"
              onClick={() => setSelectedImage(image)}
            >
              {/* Barkod Badge */}
              {image.barcode && (
                <div className="absolute top-2 left-2 bg-gray-900 text-white px-2 py-1 rounded text-xs font-mono z-10">
                  🏷️ {image.barcode}
                </div>
              )}
              <img
                src={`http://localhost:5000${image.image_url}`}
                alt={image.title}
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h4 className="font-bold text-gray-800">{image.title || 'Ürün'}</h4>
                {image.category_name && (
                  <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                    {image.category_icon || '📁'} {image.category_name}
                  </span>
                )}
                {image.description && (
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{image.description}</p>
                )}
                {image.price && (
                  <p className="text-2xl font-bold text-green-600 mt-3">{image.price} ₺</p>
                )}
                <div className="flex gap-2 mt-3">
                  <button 
                    onClick={(e) => handleAddToCart(e, image)}
                    className={`flex-1 py-2 rounded-lg transition text-sm font-semibold ${
                      addedToCart === image.id 
                        ? 'bg-green-500 text-white' 
                        : 'bg-orange-500 text-white hover:bg-orange-600'
                    }`}
                  >
                    {addedToCart === image.id ? '✓ Eklendi!' : '🛒 Sepete Ekle'}
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); navigate('/iletisim', { state: { product: image, page: page.title } }); }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                  >
                    📞
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <span className="text-6xl mb-4 block">📭</span>
          <p className="text-gray-500 text-lg">
            {searchQuery ? 'Arama sonucu bulunamadı.' : 'Bu kategoride henüz ürün bulunmamaktadır.'}
          </p>
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div 
            className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`http://localhost:5000${selectedImage.image_url}`}
              alt={selectedImage.title}
              className="w-full max-h-[60vh] object-contain"
            />
            <div className="p-6">
              {selectedImage.barcode && (
                <span className="inline-block mb-2 px-3 py-1 bg-gray-800 text-white text-sm rounded font-mono">
                  🏷️ {selectedImage.barcode}
                </span>
              )}
              <h3 className="text-2xl font-bold text-gray-800">{selectedImage.title || 'Ürün'}</h3>
              {selectedImage.description && (
                <p className="text-gray-600 mt-2">{selectedImage.description}</p>
              )}
              {selectedImage.price && (
                <p className="text-3xl font-bold text-green-600 mt-4">{selectedImage.price} ₺</p>
              )}
              <div className="flex gap-4 mt-6">
                <button 
                  onClick={(e) => {
                    handleAddToCart(e, selectedImage);
                    setSelectedImage(null);
                  }}
                  className="flex-1 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition font-semibold"
                >
                  🛒 Sepete Ekle
                </button>
                <button 
                  onClick={() => { setSelectedImage(null); navigate('/iletisim', { state: { product: selectedImage, page: page.title } }); }}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
                >
                  📞 İletişime Geç
                </button>
                <button 
                  onClick={() => setSelectedImage(null)}
                  className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== İLETİŞİM SAYFASI ====================
function ContactPage() {
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  // Check for product info from navigation state
  const state = window.history.state?.usr;

  useEffect(() => {
    if (state?.product) {
      setFormData(prev => ({
        ...prev,
        message: `"${state.product.title}" ürünü hakkında bilgi almak istiyorum. (${state.page})`
      }));
    }
  }, [state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      await apiClient.post('/contact', {
        ...formData,
        page_slug: state?.page || 'general',
        image_id: state?.product?.id
      });
      setSent(true);
    } catch (error) {
      alert('Mesaj gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <div className="bg-white rounded-xl shadow-lg p-12">
          <span className="text-6xl mb-6 block">✅</span>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Mesajınız Gönderildi!</h2>
          <p className="text-gray-600 mb-8">En kısa sürede size dönüş yapacağız.</p>
          <button
            onClick={() => navigate('/')}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
          >
            Ana Sayfaya Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <h1 className="text-4xl font-bold text-gray-800 mb-4 text-center">📞 İletişim</h1>
      <p className="text-lg text-gray-600 text-center mb-12">
        Sorularınız veya talepleriniz için aşağıdaki formu doldurun
      </p>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Contact Form */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h3 className="text-xl font-bold text-gray-800 mb-6">Bize Yazın</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Adınız Soyadınız *
              </label>
              <input
                type="text"
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Ahmet Yılmaz"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                E-posta
              </label>
              <input
                type="email"
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="ornek@email.com"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Telefon
              </label>
              <input
                type="tel"
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="0532 123 4567"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Mesajınız *
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows="5"
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Mesajınızı buraya yazın..."
                required
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold disabled:opacity-50"
            >
              {sending ? '⏳ Gönderiliyor...' : '📤 Gönder'}
            </button>
          </form>
        </div>

        {/* Contact Info */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="text-xl font-bold text-gray-800 mb-6">İletişim Bilgileri</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">📍</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Adres</p>
                  <p className="text-gray-600">Davutpaşa Mahallesi, Davutpaşa Caddesi No: 151/B, Esenler, İstanbul</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <a 
                      href="https://www.google.com/maps/search/?api=1&query=Davutpaşa+Mahallesi+Davutpaşa+Caddesi+No+151+Esenler+İstanbul"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-500 text-white text-xs rounded-full hover:bg-blue-600 transition"
                    >
                      🗺️ Google Maps
                    </a>
                    <a 
                      href="https://maps.apple.com/?q=Davutpaşa+Mahallesi+Davutpaşa+Caddesi+No+151+Esenler+İstanbul"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1 bg-gray-800 text-white text-xs rounded-full hover:bg-gray-900 transition"
                    >
                      🍎 Apple Maps
                    </a>
                    <a 
                      href="https://yandex.com.tr/harita/?text=Davutpaşa+Mahallesi+Davutpaşa+Caddesi+No+151+Esenler+İstanbul"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1 bg-red-500 text-white text-xs rounded-full hover:bg-red-600 transition"
                    >
                      🔴 Yandex Harita
                    </a>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">📞</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Telefon</p>
                  <p className="text-gray-600">0545 547 4751 - 0545 547 4793</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">📧</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">E-posta</p>
                  <p className="text-gray-600">barascopy@gmail.com</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg p-8 text-white">
            <h3 className="text-xl font-bold mb-4">🕐 Çalışma Saatleri</h3>
            <ul className="space-y-2">
              <li className="flex justify-between">
                <span>Pazartesi - Cuma</span>
                <span>08:30 - 19:30</span>
              </li>
              <li className="flex justify-between">
                <span>Cumartesi</span>
                <span>14:00 - 18:30</span>
              </li>
              <li className="flex justify-between">
                <span>Pazar</span>
                <span>14:00 - 18:30</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== DUYURULAR SAYFASI ====================
function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  // Otomatik kaydırma (birden fazla duyuru varsa)
  useEffect(() => {
    if (announcements.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % announcements.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [announcements.length]);

  const fetchAnnouncements = async () => {
    try {
      const response = await apiClient.get('/announcements');
      setAnnouncements(response.data || []);
    } catch (error) {
      console.error('Duyurular yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + announcements.length) % announcements.length);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % announcements.length);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 text-center">
        <p className="text-gray-500">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-800 mb-4">📢 Duyurular</h1>
        <p className="text-lg text-gray-600">Güncel haberler ve duyurularımız</p>
      </div>

      {announcements.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <span className="text-6xl mb-4 block">📭</span>
          <p className="text-gray-500 text-lg">Henüz duyuru bulunmamaktadır.</p>
        </div>
      ) : (
        <>
          {/* Carousel Slider */}
          <div className="relative mb-12 rounded-2xl overflow-hidden shadow-2xl">
            <div className="relative min-h-[400px] md:min-h-[500px]">
              {announcements.map((announcement, index) => (
                <div
                  key={announcement.id}
                  className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                    index === currentSlide 
                      ? 'opacity-100 translate-x-0' 
                      : index < currentSlide 
                        ? 'opacity-0 -translate-x-full' 
                        : 'opacity-0 translate-x-full'
                  }`}
                >
                  {/* Arkaplan */}
                  {announcement.image_url ? (
                    <div className="absolute inset-0">
                      <img
                        src={`http://localhost:5000${announcement.image_url}`}
                        alt={announcement.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                    </div>
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-purple-700" />
                  )}
                  
                  {/* İçerik */}
                  <div className="relative z-10 flex items-end h-full p-8 md:p-12">
                    <div className="max-w-3xl text-white">
                      <h2 className="text-3xl md:text-5xl font-bold mb-4 drop-shadow-lg">
                        {announcement.title}
                      </h2>
                      <p className="text-lg md:text-xl opacity-90 drop-shadow line-clamp-3">
                        {announcement.content}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Navigasyon Okları */}
              {announcements.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white p-3 rounded-full transition"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white p-3 rounded-full transition"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}

              {/* Nokta Göstergeleri */}
              {announcements.length > 1 && (
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                  {announcements.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`w-3 h-3 rounded-full transition-all ${
                        index === currentSlide 
                          ? 'bg-white w-8' 
                          : 'bg-white/50 hover:bg-white/75'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Tüm Duyurular Listesi */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Tüm Duyurular</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {announcements.map((announcement) => (
                <div
                  key={announcement.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition"
                >
                  {announcement.image_url && (
                    <img
                      src={`http://localhost:5000${announcement.image_url}`}
                      alt={announcement.title}
                      className="w-full h-48 object-cover"
                    />
                  )}
                  <div className="p-6">
                    <h4 className="text-xl font-bold text-gray-800">{announcement.title}</h4>
                    <p className="text-gray-600 mt-2 line-clamp-3">{announcement.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ==================== SEPETİ SIDEBAR ====================
function CartSidebar({ isOpen, onClose }) {
  const { cartItems, removeFromCart, updateQuantity, clearCart, cartTotal } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    onClose();
    navigate('/siparis');
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div className={`fixed top-0 right-0 h-full w-96 bg-white shadow-xl z-50 transform transition-transform duration-300 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b flex items-center justify-between bg-blue-600 text-white">
            <h3 className="text-xl font-bold">🛒 Sepetim</h3>
            <button onClick={onClose} className="text-white hover:text-gray-200 text-2xl">
              ✕
            </button>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-auto p-4">
            {cartItems.length === 0 ? (
              <div className="text-center py-8">
                <span className="text-6xl block mb-4">🛒</span>
                <p className="text-gray-500">Sepetiniz boş</p>
                <button 
                  onClick={onClose}
                  className="mt-4 text-blue-600 hover:text-blue-800"
                >
                  Alışverişe Devam Et →
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex gap-3 bg-gray-50 rounded-lg p-3">
                    <img
                      src={`http://localhost:5000${item.image_url}`}
                      alt={item.title}
                      className="w-20 h-20 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-800 text-sm">{item.title}</h4>
                      {item.barcode && (
                        <span className="text-xs text-gray-500 font-mono">🏷️ {item.barcode}</span>
                      )}
                      {item.price && (
                        <p className="text-green-600 font-bold">{item.price} ₺</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-6 h-6 bg-gray-200 rounded hover:bg-gray-300"
                        >
                          -
                        </button>
                        <span className="font-semibold">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-6 h-6 bg-gray-200 rounded hover:bg-gray-300"
                        >
                          +
                        </button>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="ml-auto text-red-500 hover:text-red-700 text-sm"
                        >
                          🗑️ Sil
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {cartItems.length > 0 && (
            <div className="p-4 border-t bg-gray-50">
              <div className="flex justify-between items-center mb-4">
                <span className="text-gray-600">Toplam ({cartItems.length} ürün):</span>
                <span className="text-2xl font-bold text-green-600">{cartTotal.toFixed(2)} ₺</span>
              </div>
              <button
                onClick={handleCheckout}
                className="w-full py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition"
              >
                📦 Sipariş Ver
              </button>
              <button
                onClick={clearCart}
                className="w-full py-2 text-red-600 hover:text-red-800 mt-2"
              >
                Sepeti Temizle
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ==================== SİPARİŞ SAYFASI ====================
function OrderPage() {
  const { cartItems, cartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    customer_address: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (cartItems.length === 0) {
      navigate('/');
    }
  }, [cartItems, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.customer_phone) {
      alert('Lütfen adınızı ve telefon numaranızı girin.');
      return;
    }

    setSubmitting(true);

    try {
      const orderData = {
        ...formData,
        items: cartItems.map(item => ({
          image_id: item.id,
          quantity: item.quantity,
          price: item.price || 0
        })),
        total_amount: cartTotal
      };

      await apiClient.post('/shop/orders', orderData);
      clearCart();
      navigate('/siparis-onay');
    } catch (error) {
      console.error('Sipariş hatası:', error);
      alert('Sipariş gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">📦 Sipariş Formu</h1>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Sipariş Özeti */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold mb-4">🛒 Sipariş Özeti</h2>
          <div className="space-y-4 max-h-96 overflow-auto">
            {cartItems.map((item) => (
              <div key={item.id} className="flex gap-3 border-b pb-3">
                <img
                  src={`http://localhost:5000${item.image_url}`}
                  alt={item.title}
                  className="w-16 h-16 object-cover rounded"
                />
                <div className="flex-1">
                  <h4 className="font-semibold">{item.title}</h4>
                  {item.barcode && (
                    <span className="text-xs text-gray-500 font-mono">🏷️ {item.barcode}</span>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">{item.quantity} adet</span>
                    {item.price && (
                      <span className="font-bold text-green-600">{(item.price * item.quantity).toFixed(2)} ₺</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t flex justify-between items-center">
            <span className="font-bold text-lg">Toplam:</span>
            <span className="text-2xl font-bold text-green-600">{cartTotal.toFixed(2)} ₺</span>
          </div>
        </div>

        {/* İletişim Bilgileri */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold mb-4">📋 İletişim Bilgileri</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-1">Ad Soyad *</label>
              <input
                type="text"
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:border-blue-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Telefon *</label>
              <input
                type="tel"
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:border-blue-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">E-posta</label>
              <input
                type="email"
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Adres</label>
              <textarea
                value={formData.customer_address}
                onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
                rows="2"
                className="w-full px-4 py-2 border rounded-lg focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Notlar</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows="2"
                placeholder="Sipariş ile ilgili eklemek istediğiniz notlar..."
                className="w-full px-4 py-2 border rounded-lg focus:border-blue-500 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition disabled:opacity-50"
            >
              {submitting ? '⏳ Gönderiliyor...' : '✓ Siparişi Gönder'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ==================== SİPARİŞ ONAY SAYFASI ====================
function OrderConfirmation() {
  const navigate = useNavigate();

  return (
    <div className="max-w-2xl mx-auto px-6 py-16 text-center">
      <div className="bg-white rounded-xl shadow-lg p-12">
        <span className="text-8xl block mb-6">✅</span>
        <h1 className="text-3xl font-bold text-green-600 mb-4">Siparişiniz Alındı!</h1>
        <p className="text-gray-600 text-lg mb-8">
          Siparişiniz başarıyla alındı. En kısa sürede sizinle iletişime geçeceğiz.
        </p>
        <button
          onClick={() => navigate('/')}
          className="px-8 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition"
        >
          🏠 Ana Sayfaya Dön
        </button>
      </div>
    </div>
  );
}

export default CustomerUI;
